/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import useResponsiveScreenSwitch from "../../../utils/useResponsiveScreenSwitch";
import ROLES from "../../../utils/roles";

export interface FormValues {
  email: string;
  country: string;
  firstName: string;
  lastName: string;
  timezone: string;
  headshot: string;
  dob: string;
  phoneNumber: string;
  phonePrefix: string;
  gender: string;
  ethnicity: string;
  firstGenStudent: string;
  social: {
    facebook: string;
    instagram: string;
    youtube: string;
    twitter: string;
    linkedIn: string;
  };
  disability: string[];
  category: string;
  hobbies: string[];
}

export const initialValues: FormValues = {
  email: '',
  firstName: '',
  country: '',
  lastName: '',
  timezone: '',
  headshot: '',
  dob: '',
  phoneNumber: '',
  phonePrefix: '',
  gender: '',
  ethnicity: '',
  firstGenStudent: '',
  social: {
    facebook: '',
    instagram: '',
    youtube: '',
    twitter: '',
    linkedIn: ''
  },
  disability: [],
  category: '',
  hobbies: []
};

const ProfileLayout = () => {
  const userDetails = useAppSelector((state) => state.userProfile);
  const role = localStorage.getItem('role') || '';
  const { forMobile } = useResponsiveScreenSwitch();
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'profile', label: 'Profile', route: '/app/profile' }
    ];

    if (role === ROLES.orgAdmin) {
      baseTabs.push({
        id: 'orgsettings',
        label: 'Organization Settings',
        route: '/app/profile/orgsettings'
      });
    }

    if (userDetails.data?.connection === 'Username-Password-Authentication') {
      baseTabs.push({
        id: 'security',
        label: 'Password',
        route: '/app/profile/security'
      });
    }

    return baseTabs;
  }, [role, userDetails.data?.connection]);

  const activeRoute = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/$/, '');
    const sortedTabs = [...tabs].sort((a, b) => b.route.length - a.route.length);
    const match = sortedTabs.find(
      (tab) => normalizedPath === tab.route || normalizedPath.startsWith(`${tab.route}/`)
    );
    return match?.route || '';
  }, [location.pathname, tabs]);

  const handleNavigate = (route: string) => {
    if (location.pathname !== route) {
      navigate(route);
    }
  };

  const isActive = (route: string) => activeRoute === route;
  /** For responsive */
  if (userDetails.error) {
    return <Box>{userDetails.errorText}</Box>;
  }

  if (userDetails.data) {
    return (
      <Box className='profileSettingsPage'>
        <Box className='settingsCardShell'>
          <Box className='settingsHeader'>
            <Typography className="settingsTitle">Settings</Typography>
            <Box className="settingsHeaderActions" flexDirection={forMobile ? 'column' : 'row'}>
              <Box className="settingsTabs" width={forMobile ? '100%' : 'auto'}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`settingsTab ${isActive(tab.route) ? 'active' : ''}`}
                    onClick={() => handleNavigate(tab.route)}
                    type="button"
                  >
                    {tab.label}
                  </button>
                ))}
              </Box>
              <Button className="settingsSaveBtn" variant="contained">
                Save Changes
              </Button>
            </Box>
          </Box>
          <Box className='settingsBody'>
            <Outlet />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

export default ProfileLayout;
